import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Castle, Mail, Trees, BookOpen, ScrollText, Lamp, Sparkles, Gem, Wand2, Moon, Flower2, Waves, Clapperboard, Lightbulb, Film, Sun, Star, Crown, Cake, Wine, Church } from "lucide-react";

const Icons = { Castle, Mail, Trees, BookOpen, ScrollText, Lamp, Sparkles, Gem, Wand2, Moon, Flower2, Waves, Clapperboard, Lightbulb, Film, Sun, Star, Crown, Cake, Wine, Church };
import { OPENERS } from "../../lib/inviteDesigns";
import { fileUrl } from "../site/HeroSlides";
import { WEDDING_COVERS } from "./WeddingCovers";
import { Gate, GATE_CTA } from "./InviteGates";
import { NamesFx, SceneStory } from "./InviteScenes";

const tf = (f) => `'${f}', serif`;

function Particles({ effect, color }) {
  const dots = useMemo(() => Array.from({ length: 26 }, (_, i) => ({ left: (i * 37) % 100, delay: (i * 0.73) % 9, dur: 7 + ((i * 1.9) % 7), size: 4 + ((i * 3) % 9) })), []);
  if (!effect || effect === "none") return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-30" data-testid={`invite-effect-${effect}`}>
      {dots.map((d, i) => (
        <span key={i} className={`inv-p inv-${effect}`} style={{ left: `${d.left}%`, animationDelay: `${d.delay}s`, animationDuration: `${d.dur}s`, width: d.size, height: effect === "confetti" ? d.size * 1.6 : d.size, "--c": color }} />
      ))}
    </div>
  );
}

const artBg = (art, d, a = "d9") => (art ? { backgroundImage: `linear-gradient(180deg, ${d.pal.bg}66, ${d.pal.bg}${a}), url(${fileUrl(art)})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: `radial-gradient(ellipse at top, ${d.pal.panel}, ${d.pal.bg} 70%)` });

function Closed({ d, inv, names, guest, dateLabel, onOpen }) {
  const o = OPENERS[d.opener];
  const Icon = Icons[o.icon] || Icons.Sparkles;
  const gate = inv.interaction && inv.interaction !== "tap";
  return (
    <div onClick={gate ? undefined : onOpen} role={gate ? undefined : "button"} className={`absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20 ${gate ? "" : "cursor-pointer"}`} data-testid={gate ? "invite-closed" : "invite-open"}>
      {inv.art_path && <span className="absolute inset-0 -z-20 opacity-60" style={artBg(inv.art_path, d, "99")} data-testid="invite-art-cover" />}
      <span className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-72 rounded-3xl -z-10" style={{ background: `radial-gradient(ellipse, ${d.pal.bg}f2 40%, ${d.pal.bg}00 75%)` }} />
      {gate ? <div className="mb-5"><Gate kind={inv.interaction} d={d} dateLabel={dateLabel} onOpen={onOpen} /></div> : (
        <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 2.4 }} className="w-20 h-20 rounded-full flex items-center justify-center mb-5 border-2" style={{ borderColor: d.pal.accent, color: d.pal.accent, boxShadow: `0 0 40px ${d.pal.accent}55` }}>
          <Icon className="w-9 h-9" />
        </motion.span>
      )}
      {guest && <p className="text-sm mb-2 opacity-90" style={{ color: d.pal.text }} data-testid="invite-guest-name">Dear {guest}</p>}
      <p className="text-4xl leading-tight mb-3" style={{ fontFamily: tf(d.font), color: d.pal.text }}>{names}</p>
      <p className="text-[10px] uppercase tracking-[0.3em] animate-pulse font-semibold" style={{ color: d.pal.accent, textShadow: `0 0 12px ${d.pal.bg}` }}>{gate ? GATE_CTA[inv.interaction] : o.cta}</p>
      {gate && <button onClick={onOpen} className="mt-3 text-[10px] underline opacity-60" style={{ color: d.pal.text }} data-testid="invite-open-skip">or tap here to open</button>}
    </div>
  );
}

const half = (d, side, extra = {}) => ({ background: `linear-gradient(${side === "l" ? 90 : 270}deg, ${d.pal.panel}, ${d.pal.bg})`, borderColor: d.pal.accent, ...extra });

// The cover that animates away. Each mechanic is a different opening.
function Cover({ d, opened, children }) {
  const m = OPENERS[d.opener].mech;
  const T = { duration: 1.6, ease: [0.7, 0, 0.3, 1] };
  const WC = WEDDING_COVERS[m];
  if (WC) return <WC d={d} opened={opened}>{children}</WC>;
  if (m === "doors") return (
    <div className="absolute inset-0 z-10 flex" style={{ perspective: 1400 }}>
      <motion.div className="w-1/2 h-full border-r-2" style={{ ...half(d, "l"), transformOrigin: "left" }} animate={opened ? { rotateY: -105, opacity: 0.2 } : {}} transition={T} />
      <motion.div className="w-1/2 h-full border-l-2" style={{ ...half(d, "r"), transformOrigin: "right" }} animate={opened ? { rotateY: 105, opacity: 0.2 } : {}} transition={T} />
      {!opened && children}
    </div>
  );
  if (m === "curtain" || m === "curtainY") {
    const y = m === "curtainY";
    return (
      <div className={`absolute inset-0 z-10 flex ${y ? "flex-col" : ""}`}>
        <motion.div className={y ? "h-1/2 w-full" : "w-1/2 h-full"} style={half(d, "l")} animate={opened ? (y ? { y: "-100%" } : { x: "-100%" }) : {}} transition={T} />
        <motion.div className={y ? "h-1/2 w-full" : "w-1/2 h-full"} style={half(d, "r")} animate={opened ? (y ? { y: "100%" } : { x: "100%" }) : {}} transition={T} />
        {!opened && children}
      </div>
    );
  }
  if (m === "flap") return (
    <motion.div className="absolute inset-0 z-10" style={{ background: d.pal.panel, perspective: 1200 }} animate={opened ? { y: "110%", opacity: 0 } : { y: 0, opacity: 1 }} transition={{ ...T, delay: opened ? 0.9 : 0 }}>
      <motion.div className="absolute inset-x-0 top-0 h-1/2" style={{ background: d.pal.bg, clipPath: "polygon(0 0,100% 0,50% 100%)", transformOrigin: "top", borderBottom: `2px solid ${d.pal.accent}` }} animate={opened ? { rotateX: 180 } : {}} transition={{ duration: 0.9 }} />
      {!opened && children}
    </motion.div>
  );
  if (m === "book") return (
    <div className="absolute inset-0 z-10" style={{ perspective: 1600 }}>
      <motion.div className="absolute inset-0 border-4" style={{ ...half(d, "l"), transformOrigin: "left" }} animate={opened ? { rotateY: -165, opacity: 0 } : {}} transition={T}>{!opened && children}</motion.div>
    </div>
  );
  if (m === "unroll") return (
    <motion.div className="absolute inset-0 z-10" style={{ ...half(d, "l"), transformOrigin: "top" }} animate={opened ? { scaleY: 0 } : {}} transition={T}>
      <div className="absolute inset-x-4 top-3 h-3 rounded-full" style={{ background: d.pal.accent }} /><div className="absolute inset-x-4 bottom-3 h-3 rounded-full" style={{ background: d.pal.accent }} />
      {!opened && children}
    </motion.div>
  );
  if (m === "iris") return (
    <motion.div className="absolute inset-0 z-10" style={{ background: `radial-gradient(circle, ${d.pal.panel}, ${d.pal.bg})` }} initial={{ clipPath: "circle(150% at 50% 50%)" }} animate={opened ? { clipPath: "circle(0% at 50% 50%)" } : {}} transition={T}>{!opened && children}</motion.div>
  );
  if (m === "countdown") return <Countdown d={d} opened={opened}>{children}</Countdown>;
  const Icon = Icons[OPENERS[d.opener].icon] || Icons.Sparkles;
  return (
    <motion.div className="absolute inset-0 z-10" style={{ background: `linear-gradient(180deg, ${d.pal.bg}, ${d.pal.panel})` }} animate={opened ? { opacity: 0 } : {}} transition={{ duration: 1, delay: 1.2 }}>
      {opened && [10, 25, 40, 55, 70, 85].map((l, i) => <motion.span key={l} className="absolute bottom-0" style={{ left: `${l}%`, color: d.pal.accent }} initial={{ y: 0 }} animate={{ y: "-120vh", opacity: [1, 1, 0] }} transition={{ duration: 2.2, delay: i * 0.12 }}><Icon className="w-8 h-8" /></motion.span>)}
      {!opened && children}
    </motion.div>
  );
}

function Countdown({ d, opened, children }) {
  const [n, setN] = useState(3);
  useEffect(() => {
    if (!opened) { setN(3); return undefined; }
    const t = setInterval(() => setN((x) => { if (x <= 1) clearInterval(t); return x - 1; }), 600);
    return () => clearInterval(t);
  }, [opened]);
  return (
    <AnimatePresence>{(!opened || n > 0) && (
      <motion.div exit={{ opacity: 0 }} className="absolute inset-0 z-10 flex items-center justify-center" style={{ background: d.pal.bg }}>
        {opened ? <motion.span key={n} initial={{ scale: 2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-40 h-40 rounded-full border-4 flex items-center justify-center text-7xl" style={{ borderColor: d.pal.accent, color: d.pal.text, fontFamily: tf("Playfair Display") }}>{n}</motion.span> : children}
      </motion.div>
    )}</AnimatePresence>
  );
}

function useCountdown(date) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!date) return null;
  const ms = new Date(`${date}T16:00:00`).getTime() - now;
  if (ms <= 0) return null;
  return { d: Math.floor(ms / 864e5), h: Math.floor((ms % 864e5) / 36e5), m: Math.floor((ms % 36e5) / 6e4) };
}

function Media({ media }) {
  const [i, setI] = useState(0);
  useEffect(() => { if (media.length < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % media.length), 4500); return () => clearInterval(t); }, [media.length]);
  if (!media.length) return null;
  const m = media[i % media.length];
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden my-5 shadow-2xl" data-testid="invite-media">
      <AnimatePresence mode="wait">
        {m.type === "video"
          ? <motion.video key={m.media_id} src={fileUrl(m.path)} autoPlay muted loop playsInline controls initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 w-full h-full object-cover" />
          : <motion.img key={m.media_id} src={fileUrl(m.path)} alt="" initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="absolute inset-0 w-full h-full object-cover" />}
      </AnimatePresence>
    </div>
  );
}

const KIND = { invitation: "Together with their families, invite you to celebrate their wedding", save_the_date: "Save the date", party: "You're invited to the party" };

function Content({ d, inv, wedding, guest, guestId }) {
  const cd = useCountdown(wedding.wedding_date);
  const date = wedding.wedding_date ? new Date(`${wedding.wedding_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
  const btn = { borderColor: d.pal.accent, color: d.pal.text };
  return (
    <div className="absolute inset-0 overflow-y-auto" style={{ ...artBg(inv.art_path, d), color: d.pal.text }}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1 }} className="min-h-full flex flex-col items-center justify-center text-center px-7 py-12" data-testid="invite-content">
        {guest && <p className="text-sm opacity-80 mb-4">Dear {guest},</p>}
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3" style={{ color: d.pal.accent }}>{inv.headline || KIND[inv.kind] || KIND.invitation}</p>
        <NamesFx fx={inv.name_fx} names={wedding.couple_names} d={d} />
        <Media media={inv.media || []} />
        {date && <p className="text-sm uppercase tracking-[0.25em] mt-2">{date}</p>}
        {wedding.place && <p className="text-sm opacity-80 mt-1">{wedding.place}</p>}
        {inv.message && <p className="mt-5 text-base leading-relaxed whitespace-pre-line opacity-90 max-w-sm" style={{ fontFamily: tf("Cormorant Garamond") }}>{inv.message}</p>}
        {inv.show_countdown && cd && (
          <div className="flex gap-3 mt-6" data-testid="invite-countdown">
            {[["d", "Days"], ["h", "Hours"], ["m", "Minutes"]].map(([k, l]) => <div key={k} className="w-16 py-2 rounded-xl border" style={{ borderColor: `${d.pal.accent}88` }}><p className="text-2xl" style={{ fontFamily: tf("Playfair Display") }}>{cd[k]}</p><p className="text-[9px] uppercase tracking-widest opacity-70">{l}</p></div>)}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-2 mt-7">
          {inv.show_rsvp && wedding.rsvp_open !== false && <Link to={guestId ? `/site/${wedding.slug}/rsvp/${guestId}` : `/site/${wedding.slug}`} className="px-5 py-2.5 rounded-full text-xs uppercase tracking-widest" style={{ background: d.pal.accent, color: d.pal.bg }} data-testid="invite-rsvp">RSVP</Link>}
          {inv.show_directions && wedding.place && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wedding.place)}`} target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-full text-xs uppercase tracking-widest border" style={btn} data-testid="invite-directions">Directions</a>}
          <Link to={`/site/${wedding.slug}`} className="px-5 py-2.5 rounded-full text-xs uppercase tracking-widest border" style={btn} data-testid="invite-website">Our website</Link>
        </div>
      </motion.div>
    </div>
  );
}

export function InviteStage({ design, inv, wedding, guest, guestId, musicUrl, narrationUrl, className = "", track = () => {} }) {
  const [opened, setOpened] = useState(false);
  const [replays, setReplays] = useState(0);
  const audio = useRef(null);
  const voice = useRef(null);
  useEffect(() => { setOpened(false); }, [design.id, design.opener, inv.interaction]);
  const open = () => {
    track("open", 0, 0);
    if (!inv.scenes?.some((x) => x.type !== "rsvp")) track("finish", 0, 0);
    setOpened(true);
    if (audio.current) { audio.current.volume = narrationUrl ? 0.35 : 1; audio.current.play().catch(() => {}); }
    if (voice.current) setTimeout(() => voice.current?.play().catch(() => {}), 1600);
  };
  return (
    <div className={`relative overflow-hidden ${className}`} style={{ background: design.pal.bg }} data-testid="invite-stage">
      {opened && inv.scenes?.some((x) => x.type !== "rsvp")
        ? <SceneStory key={replays} d={design} inv={inv} wedding={wedding} bg={artBg(inv.art_path, design)} onProgress={track}><Content d={design} inv={inv} wedding={wedding} guest={guest} guestId={guestId} /></SceneStory>
        : <Content d={design} inv={inv} wedding={wedding} guest={guest} guestId={guestId} />}
      <div className={`absolute inset-0 z-10 ${opened ? "pointer-events-none" : ""}`}><Cover d={design} opened={opened}><Closed d={design} inv={inv} names={wedding.couple_names} guest={guest} dateLabel={wedding.wedding_date ? new Date(`${wedding.wedding_date}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""} onOpen={open} /></Cover></div>
      <Particles effect={inv.effect} color={design.pal.accent} />
      {musicUrl && <audio ref={audio} src={musicUrl} loop />}
      {narrationUrl && <audio ref={voice} src={narrationUrl} data-testid="invite-narration" />}
      {opened && <button onClick={() => { setOpened(false); setReplays((r) => r + 1); }} className="absolute top-3 right-3 z-40 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-black/30 text-white" data-testid="invite-replay">Replay</button>}
    </div>
  );
}
